<template>
  <Modal :name="modalName" custom>
    <div class="war-room-topics-list-modal" data-test="all_topics_modal">
      <div class="war-room-topics-list-modal__header" data-test="header">
        All topics
        <CloseIcon width="24" height="24" class="war-room-topics-list-modal__close" @click="handleClose" />
      </div>
      <div
        v-for="(topics, title) in topicsByCategoryWithoutSuggested"
        :key="title"
        class="war-room-topics-list-modal__section"
      >
        <div class="war-room-topics-list-modal__title">{{ title }}</div>
        <div class="war-room-topics-list-modal__topics">
          <WarRoomTopic
            v-for="topic in topics"
            :key="topic.id"
            :topic="topic"
            size="medium"
            class="war-room-topics-list-modal__topic"
          />
        </div>
      </div>
      <div class="war-room-topics-list-modal__section">
        <div class="war-room-topics-list-modal__title">Suggested by Members</div>
        <div class="war-room-topics-list-modal__topics">
          <WarRoomTopic
            v-for="topic in topicsSuggestedByMembers"
            :key="topic.id"
            :topic="topic"
            size="medium"
            class="war-room-topics-list-modal__topic"
          />
        </div>
      </div>
    </div>
  </Modal>
</template>

<script lang="ts">
import Vue from 'vue';
import { mapState } from 'vuex';
import Modal from '@/components/common/Modal/Modal.vue';
import CloseIcon from '@/assets/svg/close.vue.svg';
import { groupBy } from '@/components/utils/core/collection';
import type { IPostTopic } from '@/types/community';
import WarRoomTopic from '@/components/partials/war-room/WarRoomTopic/WarRoomTopic.vue';
import { modalNames } from '~/components/partials/war-room/constants';
import { SUGGESTED_CATEGORY_TITLE } from '~/components/partials/war-room/WarRoomPostForm/WarRoomPostFormCategories/constants';
import { omit } from '~/components/utils/core/object';

interface IData {
  modalName: string;
}

export default Vue.extend({
  name: 'WarRoomTopicsListModal',
  components: {
    CloseIcon,
    Modal,
    WarRoomTopic,
  },
  data(): IData {
    return {
      modalName: modalNames.TOPICS_LIST,
    };
  },
  computed: {
    ...mapState('warRoom', ['topics_with_posts']),
    topicsByCategory(): Record<string, Array<IPostTopic>> {
      return groupBy(this.topics_with_posts, 'category');
    },
    topicsByCategoryWithoutSuggested(): Record<string, IPostTopic[]> {
      return omit(this.topicsByCategory, [SUGGESTED_CATEGORY_TITLE]);
    },
    topicsSuggestedByMembers(): IPostTopic[] {
      return this.topicsByCategory[SUGGESTED_CATEGORY_TITLE];
    },
  },
  methods: {
    handleClose(): void {
      this.$modal.hide(this.modalName);
    },
  },
});
</script>

<style scoped lang="scss">
.war-room-topics-list-modal {
  width: 100%;
  min-height: 100vh;
  padding: 24px 24px 0;
  background: var(--theme-color-over-card);

  @include mq('sm') {
    width: 592px;
    min-height: auto;
    border-radius: 8px;
    padding: 32px 32px 0;
  }

  &__header {
    @include header5;

    color: var(--theme-color-text);
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
  }

  &__close {
    color: var(--gray-50);
    cursor: pointer;
    transition: color 0.3s;

    &:hover {
      color: var(--gray-35);
    }
  }

  &__section {
    margin-bottom: 12px;
  }

  &__title {
    @include body-semibold;

    color: var(--theme-color-text);
    margin-bottom: 12px;
  }

  &__topics {
    display: flex;
    flex-wrap: wrap;
  }

  &__topic {
    margin: 0 8px 12px 0;
  }
}
</style>
